import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { filter, switchMap, take } from 'rxjs/operators';
import { AuthService } from '../services/auth-service';

export type GuardMode =
  | 'auth'
  | 'profile'
  | 'masterAdmin'
  | 'appAdmin'
  | 'appAdminSupport';

const resolveRouteAccess = (guardMode: GuardMode) => {
  const msalBroadcastService = inject(MsalBroadcastService);
  const msalService = inject(MsalService);
  const authService = inject(AuthService);
  const router = inject(Router);

  return msalBroadcastService.inProgress$.pipe(
    filter((status: InteractionStatus) => status === InteractionStatus.None),
    take(1),
    switchMap(async () => {
      const accounts = msalService.instance.getAllAccounts();
      if (accounts.length === 0) {
        void msalService.loginRedirect();
        return false;
      }

      if (!msalService.instance.getActiveAccount() && accounts.length > 0) {
        msalService.instance.setActiveAccount(accounts[0]);
      }

      await authService.waitForToken();

      if (guardMode === 'auth') {
        return true;
      }

      if (!authService.getRoleConfig().isProfileComplete) {
        router.navigate(['/myProfile']);
        return false;
      }

      let hasAccess = true;

      switch (guardMode) {
        case 'masterAdmin':
          hasAccess = authService.hasAppAdminSupportRole;
          break;
        case 'appAdmin':
          hasAccess = authService.hasAppAdminSupportRole;
          break;
        case 'appAdminSupport':
          hasAccess = authService.hasAppAdminSupportRole;
          break;
        case 'profile':
        default:
          hasAccess = authService.getRoleConfig()?.rolesConfig.GeneralUser
            ? false
            : true;
          break;
      }

      if (!hasAccess) {
        router.navigate(['/myProfile']);
        return false;
      }

      return true;
    }),
  );
};

export const RouteAccessGuard = (route: ActivatedRouteSnapshot) => {
  const guardMode = (route.data?.['guardMode'] as GuardMode) ?? 'profile';
  return resolveRouteAccess(guardMode);
};
